/*
  Peraan product add-ons — one cart flow for every product-page extra.

  Covers the three product-page extras:
    - Pouch ("Product Addons" block, .product-addon)            -> Task 3
    - Add-on products (custom.add_on_products metafield)        -> Task 1
    - Adult / Kids pair (<adult-mini-addon>)                    -> Task 4

  How it plugs into Impulse's default cart:
    - Classic product template: theme.AjaxProduct listens for the form "submit".
      When at least one extra is ticked we take over that submit (capture phase,
      after custom_flow's custom-size validation), add main item + every extra in
      ONE /cart/add.js request, then hand the response to theme.AjaxProduct's own
      success()/error() so the cart drawer, cart page redirect and error message
      behave exactly like a normal add to cart.
    - Flex / high-variant template: <product-form> fires "product-form:cart:request",
      which is the theme's official hook for a custom cart request. We cancel it,
      send the combined request and resolve/reject the promise it gives us.

  Buy it now uses the same cart payload, then sends the customer to checkout.
  With no extras ticked, Add to Cart is not intercepted.
  Loaded once from layout/theme.liquid so it also works in Quick Shop, where block
  HTML is injected after load and inline scripts would never run.
*/
(() => {
  'use strict';

  if (window.PeraanAddons) return;

  const CONTAINER_SELECTOR = '.product-section, product-hot-reload';
  const EXTRA_SCOPE_SELECTOR = 'peraan-addons, adult-mini-addon';

  const clean = (value) => String(value == null ? '' : value).replace(/ /g, ' ').replace(/\s+/g, ' ').trim();

  const formatMoney = (cents) => {
    const amount = Number(cents || 0);
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      const holder = document.createElement('span');
      holder.innerHTML = theme.Currency.formatMoney(amount, theme.settings && theme.settings.moneyFormat);
      return holder.textContent;
    }
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'PKR';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount / 100);
    } catch (error) {
      return `${currency} ${(amount / 100).toFixed(2)}`;
    }
  };

  // Same markup the theme writes for the main price (<span class=money>), so the currency app converts it.
  const formatMoneyHtml = (cents) => {
    const amount = Number(cents || 0);
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(amount, theme.settings && theme.settings.moneyFormat);
    }
    const holder = document.createElement('span');
    holder.textContent = formatMoney(amount);
    return holder.innerHTML;
  };

  // Rewrites only when the amount changes, so a price the currency app already converted is not reset.
  const setMoney = (element, cents, force) => {
    if (!element) return;
    const amount = String(Number(cents || 0));
    if (!force && element.dataset.moneyCents === amount) return;
    element.dataset.moneyCents = amount;
    element.innerHTML = formatMoneyHtml(amount);
  };

  const cartAddUrl = () =>
    (window.theme && theme.routes && theme.routes.cartAdd) ||
    `${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart/add.js`;

  /* ---------------------------------------------------------------------- */
  /* DOM helpers                                                             */
  /* ---------------------------------------------------------------------- */

  const getContainer = (element) =>
    (element && element.closest && element.closest(CONTAINER_SELECTOR)) ||
    (element && element.closest && element.closest('.shopify-section')) ||
    null;

  const isProductForm = (form) =>
    form && form.tagName === 'FORM' &&
    /\/cart\/add/.test(form.getAttribute('action') || '') &&
    Boolean(form.querySelector('[name="id"]'));

  const getForm = (container) => {
    if (!container) return null;
    const forms = Array.from(container.querySelectorAll('form[action*="/cart/add"]'));
    return forms.find((form) => form.querySelector('[name="id"]') && form.querySelector('[type="submit"]')) ||
      forms.find((form) => form.querySelector('[name="id"]')) ||
      null;
  };

  const getSubmitButton = (form) =>
    form && (
      form.querySelector('[data-add-to-cart]') ||
      form.querySelector('button[name="add"]') ||
      form.querySelector('button[type="submit"]')
    );

  const getButtonTextElement = (button) =>
    button && (
      button.querySelector('[data-add-to-cart-text]') ||
      button.querySelector('.element-text') ||
      button
    );

  const isVisible = (element) => {
    if (!element || element.hidden) return false;
    if (element.closest('[hidden]')) return false;
    return element.style.display !== 'none';
  };

  const getQuantity = (form) => {
    if (!form) return 1;
    const quantity = Number(new FormData(form).get('quantity') || 1);
    return quantity > 0 ? Math.floor(quantity) : 1;
  };

  /* ---------------------------------------------------------------------- */
  /* Current main variant                                                    */
  /* ---------------------------------------------------------------------- */

  const variantCache = new WeakMap();
  const variantOverride = new WeakMap();

  const getVariants = (container) => {
    const source = container && container.querySelector('[data-variant-json]');
    if (!source) return [];
    if (variantCache.has(source)) return variantCache.get(source);
    let variants = [];
    try {
      variants = JSON.parse(source.textContent || '[]');
    } catch (error) {
      variants = [];
    }
    variantCache.set(source, variants);
    return variants;
  };

  const getMainVariant = (container) => {
    const form = getForm(container);
    const id = Number(form && new FormData(form).get('id'));
    const variants = getVariants(container);
    const override = variantOverride.get(container);
    if (override && (!id || Number(override.id) === id || !variants.length)) return override;
    const fromJson = variants.find((variant) => Number(variant.id) === id);
    if (fromJson) return fromJson;

    // Flex template re-renders blocks on every variant change, so the Liquid values are current.
    const holder = container && container.querySelector('[data-peraan-main-price]');
    if (!holder) return null;
    return {
      id,
      price: Number(holder.dataset.peraanMainPrice || 0),
      title: holder.dataset.peraanMainVariantTitle || '',
      available: holder.dataset.peraanMainAvailable !== 'false'
    };
  };

  const getMainTitle = (container) => {
    const holder = container && container.querySelector('[data-peraan-main-title]');
    return clean(
      (holder && holder.dataset.peraanMainTitle) ||
      (container && container.dataset.productTitle) ||
      (document.querySelector('h1') && document.querySelector('h1').textContent)
    );
  };

  const variantIsUnstitched = (container) => {
    const variant = getMainVariant(container);
    if (variant) {
      const text = [variant.title, variant.option1, variant.option2, variant.option3].join(' ');
      return /unstitched/i.test(text);
    }
    return Array.from(container.querySelectorAll('input[type="radio"]:checked'))
      .some((input) => /unstitched/i.test(input.value));
  };

  /* ---------------------------------------------------------------------- */
  /* Collect ticked extras                                                   */
  /* ---------------------------------------------------------------------- */

  const updatePouchVisibility = (container) => {
    container.querySelectorAll('.product-addon[data-show-rule="stitched"]').forEach((addon) => {
      const visible = !variantIsUnstitched(container);
      addon.style.display = visible ? '' : 'none';
      addon.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible) return;
      addon.querySelectorAll('input').forEach((input) => { input.checked = false; });
      addon.querySelectorAll('select').forEach((select) => { select.selectedIndex = 0; });
    });
  };

  const collectPouch = (container) => {
    const extras = [];
    container.querySelectorAll('.product-addon').forEach((addon) => {
      if (!isVisible(addon)) return;

      addon.querySelectorAll('input[data-addon-variant]:checked').forEach((input) => {
        extras.push({
          id: Number(input.dataset.addonVariant),
          title: clean(input.dataset.addonTitle),
          price: Number(input.dataset.addonPrice || 0),
          property: 'Main Product'
        });
      });

      addon.querySelectorAll('select.addon-dropdown').forEach((select) => {
        const option = select.options[select.selectedIndex];
        if (!option || !option.value) return;
        extras.push({
          id: Number(option.value),
          title: clean(option.dataset.addonTitle || option.textContent),
          price: Number(option.dataset.addonPrice || 0),
          property: 'Main Product'
        });
      });
    });
    return extras;
  };

  const collectAddonProducts = (container) => {
    const extras = [];
    container.querySelectorAll('.peraan-addon-item').forEach((item) => {
      const checkbox = item.querySelector('[data-addon-check]');
      if (!checkbox || !checkbox.checked || !isVisible(item)) return;
      const select = item.querySelector('[data-addon-variant-select]');
      const option = select ? select.options[select.selectedIndex] : null;
      const id = Number(option ? option.value : checkbox.dataset.variantId);
      const variantTitle = option ? clean(option.dataset.variantTitle) : '';
      const productTitle = clean(checkbox.dataset.addonTitle);
      extras.push({
        id,
        title: variantTitle && !/^default title$/i.test(variantTitle) ? `${productTitle} - ${variantTitle}` : productTitle,
        price: Number(option ? option.dataset.price : checkbox.dataset.price) || 0,
        property: 'Main Product'
      });
    });
    return extras;
  };

  const collectPairs = (container) => {
    const extras = [];
    container.querySelectorAll('adult-mini-addon').forEach((pair) => {
      if (pair.hidden || typeof pair.selectedOption !== 'function') return;
      const checkbox = pair.querySelector('[data-pair-checkbox]');
      const option = pair.selectedOption();
      if (!checkbox || !checkbox.checked || !option || option.disabled || !option.value) return;
      const size = clean(option.dataset.size);
      const title = clean(pair.dataset.pairTitle);
      extras.push({
        id: Number(option.value),
        title: size ? `${title} - ${size}` : title,
        price: Number(option.dataset.priceCents || 0),
        property: 'Paired With',
        buttonLabel: pair.dataset.bundleButtonLabel
      });
    });
    return extras;
  };

  const collectExtras = (container) => {
    if (!container) return [];
    updatePouchVisibility(container);
    return [
      ...collectPairs(container),
      ...collectAddonProducts(container),
      ...collectPouch(container)
    ].filter((extra) => extra.id && !Number.isNaN(extra.id));
  };

  /* ---------------------------------------------------------------------- */
  /* Build the single /cart/add.js payload                                   */
  /* ---------------------------------------------------------------------- */

  const buildItems = (formData, container, extras) => {
    const mainTitle = getMainTitle(container);
    const quantity = Math.max(1, Number(formData.get('quantity') || 1));
    const mainItem = { id: Number(formData.get('id')), quantity };
    const properties = {};

    for (const [name, value] of formData.entries()) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string' && clean(value)) properties[match[1]] = value;
    }
    if (extras.length) properties['Addon Product'] = extras.map((extra) => extra.title).filter(Boolean).join(', ');
    if (Object.keys(properties).length) mainItem.properties = properties;

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) mainItem.selling_plan = sellingPlan;

    const extraItems = extras.map((extra) => ({
      id: extra.id,
      quantity,
      properties: { [extra.property]: mainTitle }
    }));

    return [mainItem, ...extraItems];
  };

  const postItems = async (items) => {
    const response = await fetch(cartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items })
    });

    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok || (data && data.status && Number(data.status) >= 400)) {
      throw data || { status: response.status, description: response.statusText };
    }
    return data;
  };

  /* ---------------------------------------------------------------------- */
  /* Classic template: hand the result to theme.AjaxProduct                  */
  /* ---------------------------------------------------------------------- */

  let processing = false;
  let buyNowProcessing = false;

  const themeAjaxContext = (form) => ({ form, args: null, addToCart: getSubmitButton(form) });

  const showSuccess = (form, data) => {
    const proto = window.theme && theme.AjaxProduct && theme.AjaxProduct.prototype;
    if (proto && typeof proto.success === 'function') {
      proto.success.call(themeAjaxContext(form), data);
    } else if (window.theme && theme.settings && theme.settings.cartType === 'page') {
      window.location = (theme.routes && theme.routes.cartPage) || '/cart';
    } else {
      form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: { product: data, addToCartBtn: getSubmitButton(form) },
        bubbles: true
      }));
    }

    if (document.body.classList.contains('template-cart')) {
      window.scrollTo(0, 0);
      window.location.reload();
    }
  };

  const showError = (form, error) => {
    const details = error && typeof error === 'object' ? error : { description: String(error || '') };
    if (!details.description) details.description = details.message || 'Unable to add to cart';

    const proto = window.theme && theme.AjaxProduct && theme.AjaxProduct.prototype;
    if (proto && typeof proto.error === 'function') {
      proto.error.call(themeAjaxContext(form), details);
    } else {
      window.alert(typeof details.description === 'string' ? details.description : details.message);
    }

    // Keep the cart drawer and header count in sync with whatever is in the cart now.
    document.dispatchEvent(new CustomEvent('cart:build'));
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    const submitter = event.submitter;
    if (!isProductForm(form) || !submitter || !submitter.matches('[data-peraan-buy-now]')) return;

    const wasPrevented = event.defaultPrevented;
    event.preventDefault();
    event.stopImmediatePropagation();
    // The custom-size handler runs first and can reject an incomplete selection.
    if (wasPrevented) return;
    if (buyNowProcessing) return;

    const formData = new FormData(form);
    if (!Number(formData.get('id'))) {
      showError(form, { description: 'Please select an available option before checkout.' });
      return;
    }

    buyNowProcessing = true;
    submitter.disabled = true;
    submitter.setAttribute('aria-busy', 'true');

    try {
      const container = getContainer(form);
      await postItems(buildItems(formData, container, collectExtras(container)));
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      window.location.assign(`${root.replace(/\/?$/, '/')}checkout`);
    } catch (error) {
      showError(form, error);
      buyNowProcessing = false;
      submitter.disabled = false;
      submitter.removeAttribute('aria-busy');
    }
  }, true);

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!isProductForm(form) || form.closest('product-form')) return;

    // custom_flow (custom size) blocks incomplete measurements with preventDefault.
    // Stop here as well so the theme does not add the product anyway.
    if (event.defaultPrevented) {
      event.stopImmediatePropagation();
      return;
    }

    const container = getContainer(form);
    const extras = collectExtras(container);
    if (!extras.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (processing) return;

    const formData = new FormData(form);
    if (!Number(formData.get('id'))) return;

    const button = getSubmitButton(form);
    processing = true;
    if (button) {
      button.classList.add('btn--loading');
      button.setAttribute('aria-busy', 'true');
    }

    try {
      const data = await postItems(buildItems(formData, container, extras));
      showSuccess(form, data);
    } catch (error) {
      showError(form, error);
    } finally {
      processing = false;
      if (button) {
        button.classList.remove('btn--loading');
        button.removeAttribute('aria-busy');
      }
    }
  }, true);

  /* ---------------------------------------------------------------------- */
  /* Flex / high-variant template: theme's own cart-request hook             */
  /* ---------------------------------------------------------------------- */

  document.addEventListener('product-form:cart:request', (event) => {
    const detail = event.detail || {};
    const container = getContainer(event.target);
    const extras = collectExtras(container);
    if (!extras.length || !detail.form || typeof detail.resolve !== 'function') return;

    event.preventDefault();
    postItems(buildItems(detail.form, container, extras))
      .then((data) => detail.resolve(data))
      .catch((error) => detail.reject(error));
  });

  /* ---------------------------------------------------------------------- */
  /* Button label, Buy It Now and pair summaries                             */
  /* ---------------------------------------------------------------------- */

  const updateButton = (container, extras, total) => {
    const button = getSubmitButton(getForm(container));
    const textElement = getButtonTextElement(button);
    if (!textElement) return;

    const applied = textElement.dataset.peraanApplied;
    if (applied === undefined || textElement.textContent !== applied) {
      textElement.dataset.peraanOriginal = textElement.textContent;
    }

    if (extras.length && !button.disabled) {
      let label = 'Add all to cart';
      if (extras.length === 1) label = clean(extras[0].buttonLabel) || 'Add both to cart';
      const text = `${label} — ${formatMoney(total)}`;
      textElement.textContent = text;
      textElement.dataset.peraanApplied = text;
      button.classList.add('peraan-bundle-button');
    } else if (applied !== undefined && textElement.textContent === applied) {
      textElement.textContent = textElement.dataset.peraanOriginal || textElement.textContent;
      delete textElement.dataset.peraanApplied;
      button.classList.remove('peraan-bundle-button');
    }
  };

  const refresh = (container) => {
    if (!container || processing) return;

    container.querySelectorAll('.peraan-addon-item').forEach((item) => {
      const checkbox = item.querySelector('[data-addon-check]');
      const select = item.querySelector('[data-addon-variant-select]');
      const option = select && select.options[select.selectedIndex];
      const priceLabel = item.querySelector('[data-addon-price-label]');
      if (option && priceLabel && option.dataset.priceLabel) priceLabel.textContent = option.dataset.priceLabel;
      item.classList.toggle('is-selected', Boolean(checkbox && checkbox.checked));
    });

    const extras = collectExtras(container);
    const mainVariant = getMainVariant(container);
    const mainPrice = Number(mainVariant ? mainVariant.price : 0);
    const quantity = getQuantity(getForm(container));
    const total = (mainPrice + extras.reduce((sum, extra) => sum + extra.price, 0)) * quantity;

    container.querySelectorAll('adult-mini-addon').forEach((pair) => {
      if (typeof pair.updateInterface === 'function') pair.updateInterface(mainPrice);
    });

    container.classList.toggle('peraan-has-extras', extras.length > 0);
    updateButton(container, extras, total);
  };

  const refreshSoon = (container) => window.setTimeout(() => refresh(container), 0);

  const refreshAll = () => {
    document.querySelectorAll(EXTRA_SCOPE_SELECTOR).forEach((element) => {
      const container = getContainer(element);
      if (container && !container.__peraanQueued) {
        container.__peraanQueued = true;
        window.setTimeout(() => {
          container.__peraanQueued = false;
          refresh(container);
        }, 0);
      }
    });
  };

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!target || !target.closest) return;
    const container = getContainer(target);
    if (!container || !container.querySelector(EXTRA_SCOPE_SELECTOR)) return;
    refreshSoon(container);
  });

  document.addEventListener('input', (event) => {
    if (event.target && event.target.name === 'quantity') {
      const container = getContainer(event.target);
      if (container && container.querySelector(EXTRA_SCOPE_SELECTOR)) refreshSoon(container);
    }
  });

  // The classic template fires variant:change before it updates the hidden id select,
  // so remember the variant from the event for the section that owns it.
  document.addEventListener('variant:change', (event) => {
    const variant = event.detail && event.detail.variant;
    document.querySelectorAll(EXTRA_SCOPE_SELECTOR).forEach((element) => {
      const container = getContainer(element);
      if (!container) return;
      const owns = variant && getVariants(container).some((item) => Number(item.id) === Number(variant.id));
      if (owns) variantOverride.set(container, variant);
    });
    refreshAll();
  });

  document.addEventListener('ajaxProduct:added', refreshAll);
  document.addEventListener('quantity:change', refreshAll);

  /* ---------------------------------------------------------------------- */
  /* <peraan-addons>: wrapper for the Pouch and Add-on product blocks        */
  /* ---------------------------------------------------------------------- */

  if (!customElements.get('peraan-addons')) {
    customElements.define('peraan-addons', class extends HTMLElement {
      connectedCallback() {
        refreshSoon(getContainer(this));
      }
    });
  }

  /* ---------------------------------------------------------------------- */
  /* <adult-mini-addon>: Adult / Kids "Complete the look" (Task 4)           */
  /* UI only — the cart request is made by the shared handler above.        */
  /* ---------------------------------------------------------------------- */

  if (!customElements.get('adult-mini-addon')) {
    customElements.define('adult-mini-addon', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.discovery === 'true') {
          this.dataset.discovery = 'loading';
          const discover = () => this.discoverPair()
            .then((pair) => {
              if (!pair || !this.hydratePair(pair)) throw new Error('No matching pair');
              this.dataset.discovery = 'false';
              this.hidden = false;
              refreshSoon(getContainer(this));
            })
            .catch(() => {
              this.dataset.discovery = 'unmatched';
              this.hidden = true;
            });
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(discover, { timeout: 1500 });
          } else {
            window.setTimeout(discover, 600);
          }
          return;
        }
        refreshSoon(getContainer(this));
      }

      disconnectedCallback() {
        const container = getContainer(this);
        if (container) refreshSoon(container);
      }

      get variantSelect() {
        return this.querySelector('[data-pair-variant]');
      }

      selectedOption() {
        const select = this.variantSelect;
        return select ? select.options[select.selectedIndex] : null;
      }

      normalizedTokens(title) {
        const ignoredWords = new Set([
          'mini', 'black', 'white', 'off', 'red', 'maroon', 'blue', 'navy', 'green', 'emerald',
          'pink', 'peach', 'lilac', 'purple', 'plum', 'yellow', 'mustard', 'orange', 'beige',
          'ivory', 'gold', 'golden', 'silver', 'grey', 'gray', 'brown', 'rust', 'teal',
          'turquoise', 'aqua', 'magenta', 'fuchsia', 'wine', 'olive'
        ]);
        return String(title || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, ' ')
          .trim()
          .split(/\s+/)
          .filter((word) => word && !ignoredWords.has(word));
      }

      async discoverPair() {
        const currentTitle = this.dataset.mainTitle || '';
        const currentHandle = this.dataset.currentHandle || '';
        const currentIsMini = /^mini\b/i.test(currentTitle);
        const tokens = this.normalizedTokens(currentTitle);
        if (!tokens.length) return null;
        const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

        for (let length = tokens.length; length > 0; length -= 1) {
          const baseQuery = tokens.slice(0, length).join(' ');
          const query = currentIsMini ? baseQuery : `Mini ${baseQuery}`;
          const url = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`;
          const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
          if (!response.ok) continue;
          const data = await response.json();
          const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];

          const match = products.find((candidate) => {
            if (!candidate || !candidate.handle || candidate.handle === currentHandle) return false;
            if (/^mini\b/i.test(candidate.title || '') === currentIsMini) return false;
            const candidateTokens = this.normalizedTokens(candidate.title);
            return candidateTokens.length === tokens.length &&
              candidateTokens.every((word, index) => word === tokens[index]);
          });

          if (match) {
            const productResponse = await fetch(`${root}products/${encodeURIComponent(match.handle)}.js`, {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' }
            });
            if (!productResponse.ok) return null;
            const productData = await productResponse.json();
            productData.url = match.url || `${root}products/${match.handle}`;
            return productData;
          }
        }
        return null;
      }

      cleanSize(title) {
        return String(title || '').split('/')[0].trim()
          .replace(/(\d+)\s*y\s*[-–—]\s*(\d+)\s*y(?:ears?)?/i, '$1–$2 Years')
          .replace(/(\d+)\s*y$/i, '$1 Years')
          .replace(/yearsears/gi, 'Years');
      }

      hydratePair(pair) {
        const variants = (pair.variants || []).filter((variant) => !/custom[\s-]*size/i.test(variant.title || ''));
        const selectedVariant = variants.find((variant) => variant.available) || variants[0];
        if (!selectedVariant) return false;

        const pairIsMini = /^mini\b/i.test(pair.title || '');
        const pairType = pairIsMini ? 'Kids' : 'Adult';
        const productUrl = pair.url || `/products/${pair.handle}`;
        this.dataset.pairTitle = pair.title;

        this.querySelectorAll('[data-pair-link]').forEach((link) => {
          link.href = productUrl;
          link.setAttribute('aria-label', pair.title);
        });
        this.querySelector('[data-pair-title]').textContent = pair.title;
        this.querySelector('[data-pair-subtitle]').textContent = pairIsMini ? 'Matching Kids Outfit' : 'Matching Adult Outfit';
        this.querySelector('[data-summary-pair-title]').textContent = pair.title;
        const sizeLabel = this.querySelector('[data-pair-size-label]');
        if (sizeLabel) sizeLabel.textContent = `${pairType} Size`;

        const imageLink = this.querySelector('.adult-mini-pair__image-link');
        const firstImage = pair.images && pair.images[0];
        const imageSource = typeof pair.featured_image === 'string'
          ? pair.featured_image
          : (pair.featured_image && pair.featured_image.src) || (typeof firstImage === 'string' ? firstImage : firstImage && firstImage.src);
        let image = imageLink.querySelector('img');
        if (imageSource) {
          if (!image) {
            image = document.createElement('img');
            image.className = 'adult-mini-pair__image';
            imageLink.appendChild(image);
          }
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = imageSource;
          image.alt = pair.title;
          image.loading = 'lazy';
          image.width = 240;
        } else if (image) {
          image.remove();
        }

        const select = this.variantSelect;
        const hasOnlyDefaultVariant = variants.length === 1 && /^default title$/i.test(variants[0].title || '');
        select.classList.toggle('visually-hidden', hasOnlyDefaultVariant);
        if (sizeLabel) sizeLabel.hidden = hasOnlyDefaultVariant;
        select.setAttribute('aria-label', `${pairType} Size`);
        select.textContent = '';
        variants.forEach((variant) => {
          const option = document.createElement('option');
          const size = this.cleanSize(variant.title);
          option.value = variant.id;
          option.dataset.priceCents = variant.price;
          option.dataset.price = formatMoney(variant.price);
          option.dataset.size = hasOnlyDefaultVariant ? '' : size;
          option.disabled = !variant.available;
          option.textContent = variant.available ? size : `${size} — ${this.dataset.soldOutLabel}`;
          option.selected = Number(variant.id) === Number(selectedVariant.id);
          select.appendChild(option);
        });
        this.querySelector('[data-pair-checkbox]').disabled = !selectedVariant.available;
        return true;
      }

      updateInterface(mainPrice) {
        const option = this.selectedOption();
        const checkbox = this.querySelector('[data-pair-checkbox]');
        if (!option || !checkbox) return;

        // Picking a sold-out size (or none) can never be "selected".
        checkbox.disabled = option.disabled;
        if (option.disabled) checkbox.checked = false;

        const pairPrice = Number(option.dataset.priceCents || 0);
        const selected = checkbox.checked;
        const main = Number(mainPrice || this.dataset.mainPrice || 0);

        setMoney(this.querySelector('[data-pair-price]'), pairPrice);
        setMoney(this.querySelector('[data-summary-main-price]'), main);
        setMoney(this.querySelector('[data-summary-pair-price]'), pairPrice);
        setMoney(this.querySelector('[data-summary-total]'), main + pairPrice);
        this.querySelector('[data-pair-summary]').hidden = !selected;
        this.classList.toggle('is-selected', selected);

        const choiceLabel = this.querySelector('[data-pair-choice-label]');
        const choiceDetail = this.querySelector('[data-pair-choice-detail]');
        if (!this.choicePrice) {
          this.choicePrice = choiceLabel.querySelector('[data-pair-choice-price]') || document.createElement('span');
          this.choicePrice.setAttribute('data-pair-choice-price', '');
        }
        choiceDetail.hidden = !selected || !option.dataset.size;
        if (selected) {
          choiceLabel.textContent = `${this.dataset.pairTitle} added to your set`;
          choiceDetail.textContent = `${this.dataset.sizePrefix} ${option.dataset.size}`;
        } else if (checkbox.disabled) {
          choiceLabel.textContent = `${this.dataset.pairTitle} — ${this.dataset.soldOutLabel}`;
        } else {
          // Keep the price as a real money element inside the label; re-render it fresh when re-attached.
          const reattached = !choiceLabel.contains(this.choicePrice);
          if (reattached) choiceLabel.replaceChildren(`Add ${this.dataset.pairTitle} (+`, this.choicePrice, ')');
          setMoney(this.choicePrice, pairPrice, reattached);
        }
      }
    });
  }

  window.PeraanAddons = { refresh, refreshAll, collectExtras, buildItems };
})();
